import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { callAPI } from '../../../network/networkConnection';

const BASE_API = process.env.REACT_APP_APIUrl || 'http://localhost:5000/api/';

// ─── Helpers ────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{7,15}$/;

function validate(form) {
  const errors = {};
  if (!form.name.trim() || form.name.trim().length < 2)
    errors.name = 'Name must be at least 2 characters';
  if (!form.email || !EMAIL_RE.test(form.email))
    errors.email = 'Enter a valid email address';
  if (!form.phone || !PHONE_RE.test(form.phone.replace(/[\s\-()]/g, '')))
    errors.phone = 'Enter a valid phone number (7–15 digits)';
  if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
    errors.amount = 'Enter a valid positive amount';
  return errors;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PaymentForm() {
  const location = useLocation();
  const autoSubmittedRef = useRef(false);

  const [form, setForm] = useState({ name: '', email: '', phone: '', amount: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const prefill = location?.state?.prefill || null;
  const autoSubmit = Boolean(location?.state?.autoSubmit);

  const submitToCcavenue = ({ encRequest, accessCode, paymentUrl }) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentUrl;
    form.style.display = 'none';

    const encInput = document.createElement('input');
    encInput.type = 'hidden';
    encInput.name = 'encRequest';
    encInput.value = encRequest;

    const accessInput = document.createElement('input');
    accessInput.type = 'hidden';
    accessInput.name = 'access_code';
    accessInput.value = accessCode;

    form.appendChild(encInput);
    form.appendChild(accessInput);
    document.body.appendChild(form);
    form.submit();
  };

  const initiatePayment = async (values) => {
    setLoading(true);
    setApiError('');

    const errors = validate(values);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setLoading(false);
      throw new Error('Validation failed');
    }

    try {
      const url = `${BASE_API}payment/initiate`;
      const headers = { 'Content-Type': 'application/json' };
      const body = JSON.stringify({
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.replace(/[\s\-()]/g, ''),
        amount: parseFloat(values.amount),
        currency: 'INR',
      });

      const response = await callAPI(url, 'POST', headers, body);
      const { encRequest: enc, access_code: code, paymentUrl: pUrl } = response?.data || {};

      if (!enc || !code || !pUrl) {
        throw new Error('Invalid response from server');
      }

      // Submit immediately with raw values (prevents React state timing issues).
      submitToCcavenue({
        encRequest: enc,
        accessCode: code,
        paymentUrl: pUrl,
      });
    } catch (err) {
      const message =
        err?.data?.status?.message ||
        err?.message ||
        'Payment initiation failed. Please try again.';
      setApiError(message);
      setLoading(false);
      throw err;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    try {
      await initiatePayment(form);
    } catch (_) {
      // error handled in initiatePayment
    }
  };

  useEffect(() => {
    if (!prefill) return;

    const nextForm = {
      name: prefill.name ?? '',
      email: prefill.email ?? '',
      phone: prefill.phone ?? '',
      amount: prefill.amount ?? '',
    };

    setForm(nextForm);

    if (autoSubmit && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      initiatePayment(nextForm).catch(() => {
        autoSubmittedRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Secure Payment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Powered by CCAvenue &nbsp;·&nbsp; 256-bit SSL encrypted
          </Typography>
        </Box>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            margin="normal"
            autoComplete="name"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            margin="normal"
            autoComplete="email"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone || 'Enter digits only, e.g. 9876543210'}
            margin="normal"
            autoComplete="tel"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Amount"
            name="amount"
            type="number"
            inputProps={{ min: 1, step: '0.01' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
            value={form.amount}
            onChange={handleChange}
            error={!!fieldErrors.amount}
            helperText={fieldErrors.amount}
            margin="normal"
            disabled={loading}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            sx={{ mt: 3, py: 1.5, fontWeight: 700, fontSize: '1rem', borderRadius: 2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Redirecting to Payment…
              </>
            ) : (
              'Pay Now'
            )}
          </Button>
        </Box>

        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          You will be redirected to the CCAvenue secure payment page to complete your transaction.
        </Typography>
      </Paper>
    </Container>
  );
}
